import React, { useEffect, useMemo, useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, Search, EventNote } from "@material-ui/icons";
import { DataGrid } from "@mui/x-data-grid";
import firebase from "../firebase";
import { dataGridSx, palette } from "../theme";
import PageContainer from "../Components/UI/PageContainer";
import PageHeader from "../Components/UI/PageHeader";

export default function KidsPage() {
  const history = useHistory();
  const signedIn = global.config.uid !== "";
  const [loading, setLoading] = useState(true);
  const [kids, setKids] = useState([]);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");

  useEffect(() => {
    if (!signedIn) return;
    const db = firebase.firestore();
    const base = db.collection("Main").doc(global.config.uid);

    const unsubKids = base
      .collection("Kids")
      .orderBy("LastName")
      .onSnapshot((snapshot) => {
        setKids(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            firstname: doc.data().FirstName,
            lastname: doc.data().LastName,
            age: doc.data().Age,
            location: doc.data().Location,
            eval: doc.data().Eval,
          }))
        );
        setLoading(false);
      });

    const unsubLocations = base.collection("Locations").onSnapshot((snapshot) => {
      setLocations(
        snapshot.docs.map((doc) => ({ id: doc.id, Location: doc.data().Location }))
      );
    });

    return () => {
      unsubKids();
      unsubLocations();
    };
  }, [signedIn]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return kids.filter((kid) => {
      if (locationFilter !== "all" && kid.location !== locationFilter) return false;
      if (!q) return true;
      const name = `${kid.firstname || ""} ${kid.lastname || ""}`.toLowerCase();
      return name.includes(q) || (kid.eval || "").toLowerCase().includes(q);
    });
  }, [kids, search, locationFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) =>
        `${params.row.firstname || ""} ${params.row.lastname || ""}`.trim(),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: palette.primarySoft,
              color: palette.primaryDark,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "0.8rem",
              flexShrink: 0,
            }}
          >
            {`${(params.row.firstname || "?")[0] || ""}${(params.row.lastname || "")[0] || ""}`}
          </Box>
          <span style={{ fontWeight: 700 }}>{params.value}</span>
        </Box>
      ),
    },
    { field: "age", headerName: "Age", width: 90 },
    {
      field: "location",
      headerName: "Location",
      width: 180,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={params.value}
            sx={{ backgroundColor: palette.primarySoft, color: palette.primaryDark }}
          />
        ) : null,
    },
    { field: "eval", headerName: "Evaluation & Concerns", flex: 1.4, minWidth: 220 },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Log a session for this kid">
          <IconButton
            aria-label={`Log session for ${params.row.firstname || "kid"}`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push("/sessions-profile?kidID=" + params.row.id);
            }}
            sx={{ color: palette.cta }}
          >
            <EventNote />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (!signedIn) return <Redirect to="/" />;

  return (
    <PageContainer>
      <PageHeader
        title="Kids"
        subtitle={`${kids.length} ${kids.length === 1 ? "kid" : "kids"} in your caseload`}
        actions={
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => history.push("/kids-profile")}
          >
            Add Kid
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={7} md={8}>
          <TextField
            fullWidth
            placeholder="Search by name or concern..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": "Search kids" }}
          />
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <TextField
            select
            fullWidth
            label="Location"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <MenuItem value="all">All locations</MenuItem>
            {locations.map((option) => (
              <MenuItem key={option.id} value={option.Location}>
                {option.Location}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <DataGrid
        autoHeight
        loading={loading}
        rows={rows}
        columns={columns}
        onRowClick={(params) => history.push("/kids-profile?kidID=" + params.id)}
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[20, 50]}
        localeText={{ noRowsLabel: "No kids yet — add your first kid to get started" }}
        sx={dataGridSx}
      />
    </PageContainer>
  );
}
